import { formatAmounts, pluralize } from '@aytm/app/stats/lib/util/helpers'
import { legendWidthFor } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/baseConfig'
import { buildColumnBarIngot } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/columnBarConfig'
import { sortData } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/sortData'
import {
  removeLogicAndTruncate,
  transformText
} from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/textUtils'
import { CHART_HEIGHT } from '@aytm/app/stats/page/question-views/components/lib/constants'

const calcTickInterval = maxYValue => {
  if (maxYValue < 12) return 1

  let tickInterval = 0
  let minTopTickDistance = maxYValue

  for (let tii = 0; tii <= 12; tii++) {
    const divisor = 15 - tii
    const topTickDistance = Math.ceil(maxYValue / divisor) * divisor - Math.ceil(maxYValue)

    if (topTickDistance >= 0 && topTickDistance < minTopTickDistance) {
      minTopTickDistance = topTickDistance
      tickInterval = Math.ceil(maxYValue / divisor)
    }
  }

  if (tickInterval === 0) {
    tickInterval = Math.round(maxYValue / 7)
  }

  return tickInterval
}

const calcGeneralData = (data, props) => {
  // the reader's percentage precision, handed to every chart from the container
  const percentDecimals = props.reportPercentDecimals

  const { qType, rType, width = 658, ignoreAmount, chartMode, viewMode, colorTheme = [], sortOrder } = props

  const answer = props.answer || {}
  let batches = props.batches?.slice() || []
  let chartLegendMode = props.chartLegendMode

  const percentsMode = Boolean(chartMode?.endsWith('percents'))

  const columnAmountsType =
    ignoreAmount || percentsMode ? (rType == null || rType === 'cases' ? 'percents' : 'respPercents') : 'amount'

  batches = sortData(answer, batches, sortOrder, { answerPath: 'b' })

  if (!batches.length || !batches[0]?.answerVariants) return

  const columnAmounts = []
  for (const av of batches[0].answerVariants) {
    if (!av) continue
    for (let bi = 0; bi < batches.length; bi++) {
      columnAmounts[bi] = columnAmounts[bi] || 0
      columnAmounts[bi] += answer[`b${batches[bi].position}`]?.[`a${av.position}`]?.[columnAmountsType] || 0
    }
  }

  let maxYValue
  if (ignoreAmount || percentsMode) {
    const max = batches.reduce((max, b) => {
      const sum = b.answerVariants.reduce((sum, av) => {
        if (b == null || av == null) return sum
        return sum + (answer[`b${b.position}`]?.[`a${av.position}`]?.[columnAmountsType] || 0)
      }, 0)

      return Math.max(max, Math.round(sum * 10) / 10)
    }, 0)

    maxYValue = Math.max(Math.ceil(max), 100)
  } else {
    const total = qType !== 'ps' ? answer.total || 0 : 0
    maxYValue = Math.ceil(Math.max(total, Math.max(...columnAmounts)))
  }

  if (chartMode !== 'brespondents' && chartMode !== 'bpercents') {
    const avLength = batches[0].answerVariants.filter(av => av != null).length
    if (avLength > 12) chartLegendMode = 'short'
  }

  const series = []
  const xAxisCategories = []
  let xIndex = 0

  const _textSize =
    chartMode === 'brespondents' || chartMode === 'bpercents' ? 12 : (80 * width) / (746 * batches.length)

  for (const av of batches[0].answerVariants) {
    if (!av) continue

    // a variant that carries a token shortens to it; shelf and rapid association name theirs by text alone
    const s = {
      name:
        qType === 'sft' || qType === 'sh'
          ? removeLogicAndTruncate(av.text, 27)
          : chartLegendMode === 'short'
            ? `A${av.position}`
            : removeLogicAndTruncate(`A${av.position}: ${av.text}`, 27),
      color: colorTheme[xIndex % colorTheme.length],
      colorIndex: xIndex,
      data: []
    }

    for (let bi = 0; bi < batches.length; bi++) {
      const b = batches[bi]
      const bText = removeLogicAndTruncate(b.text, 100)

      const avs = Object.assign({ amount: 0, percents: 0 }, answer[`b${b.position}`]?.[`a${av.position}`] || {})
      const amount = formatAmounts(avs.amount)
      const percents = (rType == null || rType === 'cases' ? avs.percents : avs.respPercents) || 0
      const y = avs[columnAmountsType]

      let desc = `<div><b>${percents.toFixed(percentDecimals ?? 1)}</b>%${
        viewMode === 'stackedCounts' ? ' <strong>of SQ</strong>' : ''
      }</div>`

      if (!ignoreAmount) {
        const unitText = rType == null || rType === 'cases' ? 'respondent' : 'response'
        desc += `<div><b>${amount}</b> ${unitText}${Number(amount) !== 1 ? 's' : ''}</div>`
      }

      if (qType === 'sft' || qType === 'sh') {
        desc += `<div class='highcharts-tooltip-text'><b>A${b.position}:</b> ${bText}</div>`
        desc += `<div class='highcharts-tooltip-text'><b>${av.text}</div>`
      } else {
        desc += `<div class='highcharts-tooltip-text'><b>SQ${b.position}:</b> ${bText}</div>`
        desc += `<div class='highcharts-tooltip-text'><b>A${av.position}:</b> ${removeLogicAndTruncate(
          av.text,
          100
        )}</div>`
      }

      s.data.push({ name: desc, x: bi, y })

      if (xIndex === 0) {
        const prefix = qType === 'sft' ? 'A' : 'SQ'
        xAxisCategories.push(chartLegendMode === 'short' ? `${prefix}${b.position}` : transformText(bText, _textSize))
      }
    }

    series.unshift(s)
    xIndex += 1
  }

  data.series = series
  data.xAxis.categories = xAxisCategories
  data.yAxis.max = maxYValue
  data.yAxis.title.text =
    ignoreAmount || percentsMode ? 'percents' : rType == null || rType === 'cases' ? 'respondents' : 'responses'
}

const buildROTooltip = (avs, amount, avText, bPosition, bText, percentDecimals) => {
  let desc = `<div><b>${avs.percents.toFixed(percentDecimals ?? 1)}</b>%</div>`
  desc += `<div><b>${amount}</b> ${pluralize(Number(amount), 'respondent', 'respondents')}</div>`
  desc += `<div class='highcharts-tooltip-text'><b>Position:</b> ${removeLogicAndTruncate(avText, 100)}</div>`
  desc += `<div class='highcharts-tooltip-text'><b>A${bPosition}:</b> ${removeLogicAndTruncate(bText, 100)}</div>`
  return desc
}

const calcROData = (data, props) => {
  // the reader's percentage precision, handed to every chart from the container
  const percentDecimals = props.reportPercentDecimals

  const {
    width,
    labelsMode = 'entities',
    answer = {},
    ignoreAmount,
    chartMode,
    colorTheme = [],
    chartLegendMode,
    sortOrder
  } = props

  // the batches are the ranked items and the answer variants the ranks: short answer labels read an
  // item as its A# token, and a rank is never shortened
  const short = chartLegendMode === 'short'

  let batches = props.batches?.slice() || []

  const percentsMode = Boolean(chartMode?.endsWith('percents'))

  let maxYValue
  if (ignoreAmount || percentsMode) {
    const max = batches.reduce((max, b) => {
      const sum = b.answerVariants.reduce((sum, av) => {
        if (b == null || av == null) return sum
        return sum + (answer[`b${b.position}`]?.[`a${av.position}`]?.percents || 0)
      }, 0)

      return Math.max(max, Math.round(sum * 10) / 10)
    }, 0)

    maxYValue = Math.max(Math.ceil(max), 100)
  } else {
    maxYValue = Math.ceil(answer.total || 0)
  }

  batches = sortData(answer, batches, sortOrder, { answerType: 'score', answerPath: 'b' })

  if (!batches.length || !batches[0]?.answerVariants) return

  const series = []
  const xAxisCategories = []
  let xIndex = 0

  if (labelsMode === 'entities') {
    const _textSize =
      chartMode === 'brespondents' || chartMode === 'bpercents'
        ? 12
        : (80 * width) / (746 * batches[0].answerVariants.length)

    for (let bi = 0; bi < batches.length; bi++) {
      const b = batches[bi]
      const s = {
        name: short ? `A${b.position}` : removeLogicAndTruncate(`A${b.position}: ${b.text}`, 27),
        color: colorTheme[xIndex % colorTheme.length],
        colorIndex: xIndex,
        data: []
      }

      for (let avi = 0; avi < b.answerVariants.length; avi++) {
        const av = b.answerVariants[avi]
        if (!av || !av.positive) continue

        const avs = Object.assign({ amount: 0, percents: 0 }, answer[`b${b.position}`]?.[`a${av.position}`] || {})
        const amount = formatAmounts(avs.amount)
        const y = ignoreAmount || percentsMode ? avs.percents : avs.amount

        s.data.push({
          name: buildROTooltip(avs, amount, av.text, b.position, b.text, percentDecimals),
          x: avi,
          y
        })
      }

      xAxisCategories.push(transformText(b.answerVariants[bi]?.text, _textSize))

      series.unshift(s)
      xIndex += 1
    }
  } else {
    const _textSize =
      chartMode === 'brespondents' || chartMode === 'bpercents' ? 12 : (80 * width) / (746 * batches.length)

    for (let avi = 0; avi < batches[0].answerVariants.length; avi++) {
      const av = batches[0].answerVariants[avi]
      if (!av || !av.positive) continue

      const s = {
        name: removeLogicAndTruncate(av.text, 27),
        color: colorTheme[xIndex % colorTheme.length],
        colorIndex: xIndex,
        data: []
      }

      for (let bi = 0; bi < batches.length; bi++) {
        const b = batches[bi]
        const avs = Object.assign({ amount: 0, percents: 0 }, answer[`b${b.position}`]?.[`a${av.position}`] || {})
        const amount = formatAmounts(avs.amount)
        const y = ignoreAmount || percentsMode ? avs.percents : avs.amount

        s.data.push({
          name: buildROTooltip(avs, amount, av.text, b.position, b.text, percentDecimals),
          x: bi,
          y
        })
      }

      const item = batches[avi]
      xAxisCategories.push(short && item ? `A${item.position}` : transformText(item?.text || '', _textSize))

      series.unshift(s)
      xIndex += 1
    }
  }

  data.series = series
  data.xAxis.categories = xAxisCategories
  data.yAxis.max = maxYValue
  data.yAxis.title.text = ignoreAmount || percentsMode ? 'percents' : 'respondents'
}

export const buildStackedColumnBarOptions = props => {
  const data = buildColumnBarIngot(props)

  const answer = props.answer || {}
  const batches = props.batches || []

  if (answer && batches.length) {
    const qType = props.qType

    if (qType === 'ro' || qType === 'aro') {
      calcROData(data, props)
    } else {
      calcGeneralData(data, props)
    }

    const maxYValue = data.yAxis.max
    if (maxYValue > 0) {
      data.yAxis.tickInterval = calcTickInterval(maxYValue)
    }

    data.legend.reversed = true
  }

  if (props.xAxisTitle != null) {
    data.xAxis.title.text = props.xAxisTitle
    if (props.xAxisTitle.length > 0) {
      data.chart.margin[2] = 60
    }
  }
  if (props.yAxisTitle != null) {
    data.yAxis.title.text = props.yAxisTitle
  }

  if (data.chart) {
    data.chart.width = props.width || 658
    data.chart.height = props.height || CHART_HEIGHT
    data.legend.width = legendWidthFor(data.chart.width)
  }

  return { options: data }
}
